import { useContext, useEffect, useState } from 'react';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import {
  params, string, shape, number, arrayOf, useLazyDataModel, bool, customType
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import THDStorageUtils, { SESSION_STORAGE } from '@thd-olt-global/thd-storage-utils';

export const useLoyaltyBenefitCheck = ({ skip: skipProp = false }) => {
  const { channel } = useContext(ExperienceContext);
  const THDCustomer = useThdCustomer();
  const { userID, svocID, customerType = 'b2c' } = THDCustomer || {};
  const [isMLAEligible, setIsMLAEligible] = useState(false);
  const channelId = channel === 'mobile' ? 2 : 1;
  const fsAiAssistantEnabled = useConfigService('fs:isAiAssistantEnabled');
  const fsStarterListEnabled = useConfigService('fs:isStarterListEnabled');
  const fsAiListBuilderNationalLaunch = useConfigService('fs:isAiListBuilderNationalLaunch');
  const MLA_BENEFIT_KEY = `mla_benefit_eligible${svocID}`;

  const isB2B = customerType?.toUpperCase() === 'B2B';
  // National launch: B2B users bypass benefit check when FS is enabled
  const isNationalLaunchB2B = fsAiListBuilderNationalLaunch && isB2B;

  const skip = skipProp || !customerType || customerType.toUpperCase() === 'B2C'
  || (!fsAiAssistantEnabled && !fsStarterListEnabled) || isNationalLaunchB2B;

  const [queryBenefits, { data, error }] = useLazyDataModel('loyaltyBenefitTags', {
    variables: {},
    fetchPolicy: 'cache-first',
    context: { withAuth: true }
  });

  const checkBenefitLocal = async () => {
    let benefit = null;
    try {
      const sessionValue = window?.sessionStorage?.getItem(MLA_BENEFIT_KEY);
      if (sessionValue) {
        const decodedValue = decodeURIComponent(sessionValue);
        const parsedValue = JSON.parse(decodedValue);
        benefit = parsedValue?.value;
        if (new Date(parsedValue?.expiry).getTime() <= Date.now()) {
          benefit = null;
        }
      }
    } catch (err) {
      benefit = null;
    }
    if (benefit && benefit !== 'undefined') {
      const benefitObj = JSON.parse(benefit);
      return benefitObj?.mlaBenefits;
    }
    return null;
  };

  const updateEligibility = (isEligible) => {
    if (isEligible !== null && isEligible !== isMLAEligible) {
      setIsMLAEligible(isEligible);
    }
  };

  const updateLocalStorage = (isEligible) => {
    try {
      THDStorageUtils.set(SESSION_STORAGE, {
        key: MLA_BENEFIT_KEY,
        value: JSON.stringify({ mlaBenefits: isEligible })
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error({
        message: `Error occurred while updating session storage - ${MLA_BENEFIT_KEY}`,
        err
      });
    }
  };

  useEffect(() => {
    const checkProjectBenefits = async () => {
      const shouldRefetchLoyalty = (hasBenefit) => {
        return ![true, false].includes(hasBenefit);
      };
      const benefitLocal = await checkBenefitLocal();
      if (shouldRefetchLoyalty(benefitLocal)) {
        await queryBenefits({
          variables: {
            userId: userID,
            customerAccountId: svocID,
            channelId,
            tagNames: ['ListBuilderAI']
          }
        });
      } else {
        updateEligibility(benefitLocal);
      }
    };
    if (!skip && userID && svocID) {
      checkProjectBenefits();
    }
  }, [userID, svocID, channelId]);

  useEffect(() => {
    if (data && !error) {
      const tags = data?.loyaltyBenefitTags || [];
      const mlaTag = tags.find((tag) => tag.tagName === 'ListBuilderAI');
      const hasMLABenefits = mlaTag?.enrolled || false;
      updateEligibility(hasMLABenefits);
      updateLocalStorage(hasMLABenefits);
    }
  }, [data, error]);

  // National launch: B2B users get access based on FS only (bypass benefit check)
  // Otherwise: require both benefit enrollment AND feature switch
  const aiAssistantEnabled = isNationalLaunchB2B
    ? fsAiAssistantEnabled
    : (isMLAEligible && fsAiAssistantEnabled);
  const starterListEnabled = isNationalLaunchB2B
    ? fsStarterListEnabled
    : (isMLAEligible && fsStarterListEnabled);

  return {
    isAiAssistantEnabled: aiAssistantEnabled,
    isStarterListEnabled: starterListEnabled
  };
};

useLoyaltyBenefitCheck.dataModel = {
  loyaltyBenefitTags: params({
    userId: customType('ID').shape({}).isRequired(),
    customerAccountId: customType('ID').shape({}).isRequired(),
    channelId: number().isRequired(),
    tagNames: arrayOf(string()).isRequired()
  }).shape(
    arrayOf(
      shape({
        tagName: string(),
        value: string(),
        enrolled: bool()
      })
    )
  ),
};
